<script setup>
import {ref, onMounted, computed, watch} from 'vue';
import axios from 'axios';
import Chart from 'chart.js/auto';
import router from "@/router";


const active_tab = ref(0);
const requestFitters = ref([]);
const stats = ref({
  request_total_count: 0,
  request_completed_count: 0,
  request_not_completed_count: 0,
  request_expired_count: 0,
  request_canceled_count: 0,
  request_expired_data: [],
  request_repeated_data: [],
  request_repeated_count: 0,
  request_count_by_kind: []
});

const selected_fields = ref({
  fitterId: '',
  startDate: '',
  endDate: '',
  datePeriod: '7'
});
const disconnectionStats = ref({
  total_request: { total: 0, completed: 0, not_completed: 0, canceled: 0 },
  disconnection_stats: { total: 0, completed: 0, not_completed: 0, canceled: 0 },
  termination_stats: { total: 0, completed: 0, not_completed: 0, canceled: 0 }
});
const selectedDisconnectionDate = ref({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1
});
const datePeriods = ref([
  { label: '1 день', value: '1' },
  { label: '7 дней', value: '7' },
  { label: '14 дней', value: '14' },
  { label: 'месяц', value: '30' }
]);

const statusChart = ref();
const kindChart = ref();
let statusChartInstance = null;
let kindChartInstance = null;

const fetchRequestRelatedLists = async () => {
  try {
    const response = await axios.get('request_related_lists/');
    if (response.data) {
      requestFitters.value = response.data.request_fitters || [];
      selected_fields.value.fitterId = requestFitters.value[0].fitter_id;
    }
  } catch (error) {
    console.error('Error fetching related lists:', error);
  }
};

const fetchFitterStats = async () => {
  try {
    const response = await axios.get('fitter_stats/', {
      params: {
        fitter_id: selected_fields.value.fitterId,
        start_date: selected_fields.value.startDate,
        end_date: selected_fields.value.endDate,
      }
    });
    stats.value = response.data;
    updateCharts();

    // Save filters after successful fetch
    saveFiltersToLocalStorage(selected_fields.value);
  } catch (error) {
    console.error('Error fetching fitter stats:', error);
  }
};

const fetchDisconnectionStats = async () => {
  try {
    const response = await axios.get('fitter_stats_disconnections/', {
      params: {
        fitter_id: selected_fields.value.fitterId,
        year: selectedDisconnectionDate.value.year,
        month: selectedDisconnectionDate.value.month
      }
    });
    disconnectionStats.value = response.data;
  } catch (error) {
    console.error("Error fetching disconnection stats:", error);
  }
};

const setDatePeriod = (period) => {
  selected_fields.value.datePeriod = period;

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - parseInt(period));

  selected_fields.value.startDate = formatDate(startDate);
  selected_fields.value.endDate = formatDate(endDate);

  fetchFitterStats();
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Utility functions for localStorage
const saveFiltersToLocalStorage = (filters) => {
  localStorage.setItem('fitterStatsFilters', JSON.stringify(filters));
};

const loadFiltersFromLocalStorage = () => {
  const savedFilters = localStorage.getItem('fitterStatsFilters');
  return savedFilters ? JSON.parse(savedFilters) : null;
};

const updateCharts = () => {
  if (statusChartInstance) statusChartInstance.destroy();
  if (kindChartInstance) kindChartInstance.destroy();

  if (statusChart.value) {
    const ctx = statusChart.value.getContext('2d');
    statusChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Завершен', 'В процессе', 'Отменено'],
        datasets: [{
          data: [
            stats.value.request_completed_count,
            stats.value.request_not_completed_count,
            stats.value.request_canceled_count
          ],
          backgroundColor: [
            '#198754', '#ffc107', '#6c757d'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { position: 'bottom' }
        }
      }
    });
  }

  if (kindChart && stats.value.request_count_by_kind.length > 0) {
    const ctx = kindChart.value.getContext('2d');
    const sortedKinds = [...stats.value.request_count_by_kind]
        .sort((a, b) => b.count - a.count)
        .slice(0, 7);

    const othersCount = stats.value.request_count_by_kind
        .slice(7)
        .reduce((sum, item) => sum + item.count, 0);

    if (othersCount > 0) {
      sortedKinds.push({ request_kind_name: 'Другие', count: othersCount });
    }

    kindChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: sortedKinds.map(item => item.request_kind_name),
        datasets: [{
          label: 'Кол-во запросов',
          data: sortedKinds.map(item => item.count),
          backgroundColor: '#28a745',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        scales: { y: { beginAtZero: true }},
        plugins: { legend: { display: false }}
      }
    });
  }
};

const summaryCards = computed(() => [
  { title: 'Всего заявок', value: stats.value.request_total_count || 0, bg: 'bg-primary' },
  { title: 'Завершено', value: stats.value.request_completed_count || 0, bg: 'bg-success' },
  { title: 'В процессе', value: stats.value.request_not_completed_count || 0, bg: 'bg-warning' },
  { title: 'Отменено', value: stats.value.request_canceled_count || 0, bg: 'bg-secondary' }
]);

const toRequestDetails = (request_id) => {
  router.push({
    name: 'request details',
    query: {request_id}
  });
};

// Generate years for dropdown (current year and previous 5 years)
const years = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({length: 6}, (_, i) => currentYear - i);
});

// Months for dropdown
const months = [
  { value: 1, name: 'Январь' },
  { value: 2, name: 'Февраль' },
  { value: 3, name: 'Март' },
  { value: 4, name: 'Апрель' },
  { value: 5, name: 'Май' },
  { value: 6, name: 'Июнь' },
  { value: 7, name: 'Июль' },
  { value: 8, name: 'Август' },
  { value: 9, name: 'Сентябрь' },
  { value: 10, name: 'Октябрь' },
  { value: 11, name: 'Ноябрь' },
  { value: 12, name: 'Декабрь' }
];

watch(() => selectedDisconnectionDate.value, fetchDisconnectionStats, { deep: true });
watch(() => selected_fields.value.fitterId, fetchDisconnectionStats);

onMounted(async () => {
  await fetchRequestRelatedLists();
  await fetchDisconnectionStats();
  // Try to load saved filters
  const savedFilters = loadFiltersFromLocalStorage();
  if (savedFilters) {
    selected_fields.value = {
      ...selected_fields.value,
      ...savedFilters
    };
  }

  // Apply the filters (either saved or default)
  setDatePeriod(selected_fields.value.datePeriod || '7');
});
</script>
<template>
  <div class="content-wrapper mt-5 pt-3 px-3">
    <div class="row">
      <div class="col-md-12">
        <div class="card rounded-3 shadow-sm">
          <div class="card-header bg-primary text-white rounded-top">
            <h5 class="mb-0">Статистика по монтёрам</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <!-- Fitter Select -->
              <div class="col-md-4">
                <label class="form-label fw-semibold">Выберите монтёра</label>
                <select class="form-select" v-model="selected_fields.fitterId" @change="fetchFitterStats">
                  <option v-for="fitter in requestFitters" :key="fitter.fitter_id" :value="fitter.fitter_id">
                    {{ fitter.fitter_name }}
                  </option>
                </select>
              </div>

              <!-- Date Period -->
              <div class="col-md-4">
                <label class="form-label fw-semibold">Временной промежуток</label>
                <div class="btn-group w-100" role="group">
                  <button
                      v-for="period in datePeriods"
                      :key="period.value"
                      type="button"
                      class="btn btn-outline-primary"
                      :class="{ active: selected_fields.datePeriod === period.value }"
                      @click="setDatePeriod(period.value)"
                  >
                    {{ period.label }}
                  </button>
                </div>
              </div>

              <!-- Custom Range -->
              <div class="col-md-4">
                <label class="form-label fw-semibold">Другой промежуток</label>
                <div class="input-group">
                  <input type="date" class="form-control" v-model="selected_fields.startDate">
                  <span class="input-group-text">до</span>
                  <input type="date" class="form-control" v-model="selected_fields.endDate">
                  <button class="btn btn-primary" @click="fetchFitterStats">Применить</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Chart Section -->
    <div class="row">
      <div class="col-md-12">
        <div class="card rounded-3 shadow-sm h-100">
          <div class="card-header bg-secondary text-white rounded-top">
            <h5 class="mb-0">По статусу</h5>
          </div>
          <div class="card-body row g-4">
            <!-- Chart and Cards -->
            <div class="col-md-7 col-lg-7">
              <canvas ref="statusChart"></canvas>
              <div class="row mt-3 g-3">
                <div class="col-6 col-md-3" v-for="card in summaryCards.slice(0, 2)" :key="card.title">
                  <div :class="['card', 'text-white', card.bg, 'h-100', 'rounded-2']">
                    <div class="card-body text-center">
                      <h6 class="card-title mb-2">{{ card.title }}</h6>
                      <h2 class="card-text">{{ card.value }}</h2>
                    </div>
                  </div>
                </div>
                <div class="col-6 col-md-3" v-for="card in summaryCards.slice(2, 4)" :key="card.title">
                  <div :class="['card', 'text-white', card.bg, 'h-100', 'rounded-2']">
                    <div class="card-body text-center">
                      <h6 class="card-title mb-2">{{ card.title }}</h6>
                      <h2 class="card-text">{{ card.value }}</h2>
                    </div>
                  </div>
                </div>
                <div class="col-6 col-md-3" v-for="card in summaryCards.slice(4)" :key="card.title">
                  <div :class="['card', 'text-white', card.bg, 'h-100', 'rounded-2']">
                    <div class="card-body text-center">
                      <h6 class="card-title mb-2">{{ card.title }}</h6>
                      <h2 class="card-text">{{ card.value }}</h2>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabs for Expired and Repeated Requests -->
            <div class="col-12 col-md-5">
              <div class="col-md-12">
                <div class="card rounded-3 shadow-sm h-100" style="max-height: 500px;">
                  <div class="card-header rounded-top d-flex align-items-center"
                       :class="active_tab === 0 ? 'bg-danger text-white' : 'bg-info text-white'">
                    <ul class="nav nav-tabs card-header-tabs">
                      <li class="nav-item">
                        <a class="nav-link"
                           :class="[active_tab === 0 ? 'active text-black' : 'text-light']"
                           @click="active_tab = 0">
                          Просроченные
                        </a>
                      </li>
                      <li class="nav-item">
                        <a class="nav-link"
                           :class="[active_tab === 1 ? 'active text-black' : 'text-light']"
                           @click="active_tab = 1">
                          Повторные
                        </a>
                      </li>
                    </ul>
                    <span class="badge rounded-pill ms-auto bg-white"
                          :class="active_tab === 0 ? 'text-danger' : 'text-info'">
                      {{ active_tab === 0 ? stats.request_expired_count : stats.request_repeated_count }}
                    </span>
                  </div>

                  <div class="card-body p-0 d-flex flex-column" style="overflow: hidden; height: 100%;">
                    <!-- Expired Requests Tab -->
                    <div v-if="active_tab === 0" class="flex-grow-1" style="overflow-y: auto;">
                      <div class="list-group list-group-flush">
                        <div v-for="request in stats.request_expired_data"
                             :key="request.request_id"
                             class="list-group-item d-flex justify-content-between align-items-center py-3 px-3 hover-bg">
                          <div class="d-flex flex-column">
                            <a href="#" @click.prevent="toRequestDetails(request.request_id)"
                               class="fw-semibold text-dark text-decoration-none hover-danger">
                              {{ request.street }} {{ request.house }}{{ request.flat ? ', кв. ' + request.flat : '' }}
                            </a>
                            <small class="text-muted mt-1">{{ request.request_kind }}</small>
                          </div>
                        </div>
                        <div v-if="stats.request_expired_data.length === 0" class="text-center py-4 text-muted">
                          <i class="fas fa-check-circle fa-2x mb-2"></i>
                          <p class="mb-0">Нет просроченных заявок</p>
                        </div>
                      </div>
                    </div>

                    <!-- Repeated Requests Tab -->
                    <div v-if="active_tab === 1" class="flex-grow-1" style="overflow-y: auto;">
                      <div class="list-group list-group-flush">
                        <div v-for="request in stats.request_repeated_data"
                             :key="request.request_id"
                             class="list-group-item d-flex justify-content-between align-items-center py-3 px-3 hover-bg">
                          <div class="d-flex flex-column">
                            <a href="#" @click.prevent="toRequestDetails(request.request_id)"
                               class="fw-semibold text-dark text-decoration-none hover-primary">
                              {{ request.street }} {{ request.house }}{{ request.flat ? ', кв. ' + request.flat : '' }}
                            </a>
                            <small class="text-muted mt-1">
                              {{ request.request_kind || 'Не указан вид заявки' }}
                            </small>
                          </div>
                        </div>
                        <div v-if="stats.request_repeated_data.length === 0" class="text-center py-4 text-muted">
                          <i class="fas fa-check-circle fa-2x mb-2"></i>
                          <p class="mb-0">Нет повторных заявок</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Kind Chart -->
    <div class="row mt-4">
      <div class="col-md-12">
        <div class="card rounded-3 shadow-sm h-100">
          <div class="card-header bg-secondary text-white rounded-top">
            <h5 class="mb-0">По виду</h5>
          </div>
          <div class="card-body">
            <canvas ref="kindChart"></canvas>
          </div>
        </div>
      </div>
    </div>
    <!-- Disconnection Stats Cards -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="card shadow-sm rounded-3 overflow-hidden">
          <div class="card-header bg-info text-white">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Откл/раст</h5>
              <div class="d-flex gap-2">

                <!-- Month Select -->
                <select class="form-select form-select-sm" v-model="selectedDisconnectionDate.month" style="width: 120px;">
                  <option v-for="month in months" :key="month.value" :value="month.value">
                    {{ month.name }}
                  </option>
                </select>

                <!-- Year Select -->
                <select class="form-select form-select-sm" v-model="selectedDisconnectionDate.year" style="width: 100px;">
                  <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="card-body bg-light">
            <div class="row row-cols-1 row-cols-md-3 g-4">

              <!-- Общие -->
              <div class="col">
                <div class="h-100 p-3 bg-white border rounded-3 shadow-sm">
                  <h6 class="text-uppercase text-secondary mb-3">Общие</h6>
                  <div class="d-flex justify-content-between mb-2 fs-6">
                    <span><strong>Всего:</strong></span>
                    <span>{{ disconnectionStats.total_request.total }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-success mb-2 fs-6">
                    <span><i class="bi bi-check-circle-fill me-1"></i>Завершено:</span>
                    <span>{{ disconnectionStats.total_request.completed }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-warning mb-2 fs-6">
                    <span><i class="bi bi-hourglass-split me-1"></i>В процессе:</span>
                    <span>{{ disconnectionStats.total_request.not_completed }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-danger fs-6">
                    <span><i class="bi bi-x-circle-fill me-1"></i>Отменено:</span>
                    <span>{{ disconnectionStats.total_request.canceled }}</span>
                  </div>
                </div>
              </div>

              <!-- Отключения -->
              <div class="col">
                <div class="h-100 p-3 bg-white border rounded-3 shadow-sm">
                  <h6 class="text-uppercase text-secondary mb-3">Отключения</h6>
                  <div class="d-flex justify-content-between mb-2 fs-6">
                    <span><strong>Всего:</strong></span>
                    <span>{{ disconnectionStats.disconnection_stats.total }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-success mb-2 fs-6">
                    <span><i class="bi bi-check-circle-fill me-1"></i>Завершено:</span>
                    <span>{{ disconnectionStats.disconnection_stats.completed }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-warning mb-2 fs-6">
                    <span><i class="bi bi-hourglass-split me-1"></i>В процессе:</span>
                    <span>{{ disconnectionStats.disconnection_stats.not_completed }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-danger fs-6">
                    <span><i class="bi bi-x-circle-fill me-1"></i>Отменено:</span>
                    <span>{{ disconnectionStats.disconnection_stats.canceled }}</span>
                  </div>
                </div>
              </div>

              <!-- Расторжения -->
              <div class="col">
                <div class="h-100 p-3 bg-white border rounded-3 shadow-sm">
                  <h6 class="text-uppercase text-secondary mb-3">Расторжения</h6>
                  <div class="d-flex justify-content-between mb-2 fs-6">
                    <span><strong>Всего:</strong></span>
                    <span>{{ disconnectionStats.termination_stats.total }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-success mb-2 fs-6">
                    <span><i class="bi bi-check-circle-fill me-1"></i>Завершено:</span>
                    <span>{{ disconnectionStats.termination_stats.completed }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-warning mb-2 fs-6">
                    <span><i class="bi bi-hourglass-split me-1"></i>В процессе:</span>
                    <span>{{ disconnectionStats.termination_stats.not_completed }}</span>
                  </div>
                  <div class="d-flex justify-content-between text-danger fs-6">
                    <span><i class="bi bi-x-circle-fill me-1"></i>Отменено:</span>
                    <span>{{ disconnectionStats.termination_stats.canceled }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.05);
}
.card-header {
  font-weight: 600;
}
.btn-group .btn {
  flex: 1;
}
canvas {
  max-height: 300px;
  width: 100% !important;
}
.hover-bg:hover {
  background-color: rgba(13, 110, 253, 0.05);
}
.hover-bg-danger:hover {
  background-color: rgba(220, 53, 69, 0.05);
}
.rounded-top {
  border-top-left-radius: 0.5rem !important;
  border-top-right-radius: 0.5rem !important;
}
/* For expired requests (red hover) */
.hover-danger:hover {
  color: #dc3545 !important; /* Bootstrap danger color */
  transition: color 0.2s ease;
}

/* For repeated requests (blue hover) */
.hover-primary:hover {
  color: #0d6efd !important; /* Bootstrap primary color */
  transition: color 0.2s ease;
}

/* Optional: Add underline on hover */
a.text-decoration-none:hover {
  text-decoration: underline !important;
}
</style>